import { useState, useEffect, useRef, useMemo } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "~/components/ui/sheet";
import { Menu, Minus } from "lucide-react";
import Logo from "~/assets/global/logo";
import { cn } from "~/lib/utils";
import { SITE_URL } from "~/assets/profile/profile";
import { useLocation } from "@remix-run/react";
import GlobalContactWhatsappFloating from "../global/GlobalContactWhatsappFloating";
import { navLinks } from "./NavbarLinks";

export const Navbar = () => {
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState<string>("");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isScrollingDown, setIsScrollingDown] = useState(false);
  const lastScrollYRef = useRef(0);

  // Set current page
  useEffect(() => {
    setCurrentPage(`${SITE_URL}${location.pathname}`);
  }, [location.pathname]);

  // Scroll detection
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const scrollThreshold = window.innerHeight * 0.05;

      if (Math.abs(scrollY - lastScrollYRef.current) > scrollThreshold) {
        setIsScrollingDown(scrollY > lastScrollYRef.current);
        lastScrollYRef.current = scrollY;
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navigateToRoute = (path: string) => {
    window.location.href = path;
  };

  const toggleDrawer = () => {
    setIsDrawerOpen((prev) => !prev);
  };

  return (
    <>
      <section className="w-full fixed top-0 z-50 bg-black shadow-lg">
        <div className="w-global mx-auto">
          <nav className="flex justify-between items-center px-4 py-2">
            {/* Desktop Logo */}
            <div className="hidden lg:block">
              <Logo linkTo="/" variant="light" className="max-w-[15rem]" />
            </div>

            {/* Mobile Logo with Scroll Effect */}
            <div
              className={`block lg:hidden transition-all duration-300 ${
                isScrollingDown
                  ? "min-w-[7rem] max-w-[7rem]"
                  : "py-[0.3rem] w-[17rem] min-w-[17rem] max-w-[17rem]"
              }`}
            >
              <Logo linkTo="/" variant="light" />
            </div>

            {/* Desktop Navigation */}
            <ul className="hidden lg:flex items-center space-x-8">
              {navLinks.map((link) => (
                <div key={link.id}>
                  <button
                    onClick={() => navigateToRoute(link.path)}
                    className="rounded-lg text-white font-semibold hover:bg-[#FAFF00] hover:text-black px-4 py-2 flex items-center gap-2"
                  >
                    <li>{link.label}</li>
                    {link.label === "Konsultasi" && (
                      <span className="bg-red-600 text-white text-xs px-2 py-1 rounded-md">
                        Gratis
                      </span>
                    )}
                  </button>
                </div>
              ))}
              <div className="max-w-[15rem]">
                <GlobalContactWhatsappFloating
                  currentPage={currentPage}
                  buttonText="Konsultasi Gratis"
                />
              </div>
            </ul>

            {/* Mobile Drawer Toggle Button */}
            <div
              className="pr-[1rem] text-yellow-500 block lg:hidden cursor-pointer relative"
              onClick={toggleDrawer}
            >
              <div
                className={cn(
                  "absolute -top-3 transition-opacity duration-300",
                  isScrollingDown ? "opacity-0" : "opacity-100"
                )}
              >
                <Menu size={27} strokeWidth={1.5} />
              </div>
              <div
                className={cn(
                  "absolute -top-3 transition-opacity duration-300",
                  isScrollingDown ? "opacity-100" : "opacity-0"
                )}
              >
                <Minus size={27} strokeWidth={1.5} />
              </div>
            </div>
          </nav>
        </div>
      </section>

      {/* Mobile Drawer */}
      <Sheet open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
        <SheetContent className="bg-white p-[2rem]">
          <SheetHeader>
            <SheetTitle>
              <Logo linkTo="/" variant="light" />
            </SheetTitle>
          </SheetHeader>
          <ul className="mt-10 flex flex-col gap-4">
            {navLinks.map((link) => (
              <li
                key={link.id}
                className="text-gray-800 text-sm hover:text-gray-500 font-semibold"
              >
                <button
                  onClick={() => {
                    navigateToRoute(link.path);
                    toggleDrawer();
                  }}
                  className="w-full text-left"
                >
                  {link.label}
                  {link.label === "Konsultasi" && (
                    <span className="ml-[0.5rem] bg-red-600 text-white text-xs px-2 py-1 rounded-md">
                      Gratis
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
          <div className="pt-[2rem]">
            <GlobalContactWhatsappFloating
              currentPage={currentPage}
              buttonText="Konsultasi Gratis"
            />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
